import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import clsx from 'clsx'
import {
  ChevronDownIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline'
import { manualChapters, searchManual, type ManualChapter } from '@/data/manual'

interface HelpSidebarProps {
  onSectionClick?: () => void
}

export default function HelpSidebar({ onSectionClick }: HelpSidebarProps) {
  const { chapterId, sectionId } = useParams()
  const [expandedChapters, setExpandedChapters] = useState<string[]>([chapterId || 'introduction'])
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<ReturnType<typeof searchManual>>([])

  const toggleChapter = (id: string) => {
    setExpandedChapters(prev =>
      prev.includes(id)
        ? prev.filter(c => c !== id)
        : [...prev, id]
    )
  }

  const handleSearch = (query: string) => {
    setSearchQuery(query)
    if (query.length >= 2) {
      const results = searchManual(query)
      setSearchResults(results)
    } else {
      setSearchResults([])
    }
  }

  const getChapterIcon = (chapter: ManualChapter) => {
    const icons: Record<string, string> = {
      introduction: '1',
      'getting-started': '2',
      dashboard: '3',
      forum: '4',
      courses: '5',
      documents: '6',
      events: '7',
      taskforce: '8',
      clinic: '9',
      gamification: '10',
      admin: '11',
      appendix: '12',
    }
    return icons[chapter.id] || chapter.number.toString()
  }

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="p-4 border-b">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Cerca nel manuale..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="p-4 border-b bg-gray-50">
          <p className="text-xs font-medium text-gray-500 mb-2">
            {searchResults.length} risultati trovati
          </p>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {searchResults.slice(0, 10).map((result, idx) => (
              <Link
                key={`${result.chapterId}-${result.sectionId}-${idx}`}
                to={`/help/${result.chapterId}/${result.sectionId}`}
                className="block p-2 text-sm bg-white rounded border hover:border-primary-300 hover:bg-primary-50"
                onClick={() => {
                  setSearchQuery('')
                  setSearchResults([])
                  onSectionClick?.()
                }}
              >
                <p className="font-medium text-gray-900">{result.sectionTitle}</p>
                <p className="text-xs text-gray-500">{result.chapterTitle}</p>
                {result.excerpt && (
                  <p className="text-xs text-gray-400 mt-1 line-clamp-2">{result.excerpt}</p>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Table of Contents */}
      <nav className="flex-1 overflow-y-auto p-4">
        <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Indice</p>
        <ul className="space-y-1">
          {manualChapters.map((chapter) => (
            <li key={chapter.id}>
              <button
                onClick={() => toggleChapter(chapter.id)}
                className={clsx(
                  'w-full flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                  chapterId === chapter.id
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-100'
                )}
              >
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-gray-200 rounded text-xs font-bold">
                  {getChapterIcon(chapter)}
                </span>
                <span className="flex-1 text-left truncate">{chapter.title}</span>
                {expandedChapters.includes(chapter.id) ? (
                  <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                )}
              </button>

              {/* Sections */}
              {expandedChapters.includes(chapter.id) && (
                <ul className="ml-8 mt-1 space-y-0.5">
                  {chapter.sections.map((section) => (
                    <li key={section.id}>
                      <Link
                        to={`/help/${chapter.id}/${section.id}`}
                        className={clsx(
                          'block px-3 py-1.5 text-sm rounded-md transition-colors',
                          chapterId === chapter.id && sectionId === section.id
                            ? 'bg-primary-50 text-primary-700 font-medium'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        )}
                        onClick={onSectionClick}
                      >
                        {section.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </nav>
    </div>
  )
}
